import type { CampaignPriceChangeReason } from '@/interface/campaign';

import { mapRemoteReason } from './campaign-discount-preview';

/**
 * Whether the partner discount reached the amount the API actually charged, read off a checkout
 * response (SCRUM-1760, `docs/api-handoff/2026-09-15-campaign-discount-fields-checkout.md`).
 *
 * The two fields are top-level in the HTTP body, but our thunks disagree about what they keep: some
 * return the whole body under `data`, `paddle.tsx` spreads it into the payload root, and
 * `coin-payments.tsx` puts it under `res`. So this looks in the three places rather than making
 * twenty call sites agree — and it answers `null` for everything that is not a campaign refusal,
 * which is what lets one matcher watch every fulfilled thunk in the app.
 */
const CAMPAIGN_APPLIED_KEY = 'campaign_discount_applied';
const CAMPAIGN_REASON_KEY = 'campaign_discount_refused_reason';

function readRefusalFrom(source: unknown): CampaignPriceChangeReason | null {
  if (typeof source !== 'object' || source === null) {
    return null;
  }

  const body = source as Record<string, unknown>;

  // Absent means "there was no campaign in play", which is not a refusal. Only an explicit `false`
  // is one — the API never sends `null` (it omits both keys instead).
  if (body[CAMPAIGN_APPLIED_KEY] !== false) {
    return null;
  }

  const reason = body[CAMPAIGN_REASON_KEY];

  return mapRemoteReason(typeof reason === 'string' ? reason : undefined);
}

export function readCampaignChargeRefusal(payload: unknown): CampaignPriceChangeReason | null {
  if (typeof payload !== 'object' || payload === null) {
    return null;
  }

  const envelope = payload as Record<string, unknown>;

  return readRefusalFrom(envelope) ?? readRefusalFrom(envelope.data) ?? readRefusalFrom(envelope.res);
}
